// La ficha de un producto que ya está en Tiendanube, armada con lo que hoy está publicado
// (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento). La regla de qué va en cada
// campo es pura (`fichaDesdeLaTienda`); acá se escribe.
//
// Dos caminos:
//   · al SUMAR un producto que ya está en la tienda a un lanzamiento (`completarConLaTienda`): la
//     ficha arranca igual a la tienda, así lo que después se marca como cambio es sólo lo que tocó la
//     persona. Antes arrancaba con los datos del catálogo de Atlas y todo hubiera aparecido "cambiado".
//   · "Traer lo de la tienda" de la ficha (`traerLoDeLaTienda`): vuelve a ponerla como la tienda, para
//     los productos sumados antes de esto o para deshacer todo.

import { leerPublicadosEnTn } from "../../lib/tiendaNube";
import { supabase } from "../../lib/supabase";
import { fichaDesdeLaTienda, type ProductoEnLaTienda } from "./cambiosContraLaTienda";
import {
  actualizarFicha,
  actualizarVariante,
  agregarVariante,
  fetchCategorias,
  guardarBaseDeLaTienda,
  marcarCategoriaDeProducto,
  quitarVariante,
  type CamposDeFicha,
  type CategoriaDeTienda,
} from "./lanzamientosApi";

const clave = (valores: readonly string[]) => valores.map((v) => (v ?? "").trim().toLowerCase()).join("\u0000");

/**
 * Deja la ficha (campos, variantes y categorías) igual a lo publicado. Las variantes se cruzan por sus
 * valores: la que ya estaba conserva su id (y sus fotos asignadas); las que la tienda no tiene se
 * quitan. Devuelve el primer error, o null.
 */
export async function traerLoDeLaTienda(
  producto: { id: string; variantes: ReadonlyArray<{ id: string; valores: string[] }>; categorias: readonly string[] },
  publicado: ProductoEnLaTienda,
  arbol: readonly CategoriaDeTienda[],
  companyId: string,
): Promise<{ error: string | null }> {
  const f = fichaDesdeLaTienda(publicado, arbol);
  const r = await actualizarFicha(producto.id, f.campos as unknown as CamposDeFicha);
  if (r.error) return r;
  // La ficha arranca igual a la tienda: esto es con qué se compara después lo que tocó la persona
  // (ADR-2026-10-23-publicar-manda-solo-lo-que-tocaste).
  await guardarBaseDeLaTienda(producto.id, publicado);

  const actuales = new Map(producto.variantes.map((v) => [clave(v.valores), v.id]));
  const quedan = new Set<string>();
  for (const [orden, v] of f.variantes.entries()) {
    const { valores, ...campos } = v;
    let id = actuales.get(clave(valores)) ?? null;
    if (!id) id = await agregarVariante(producto.id, companyId, valores, orden);
    if (!id) return { error: "No se pudo traer una variante de la tienda" };
    quedan.add(id);
    const rv = await actualizarVariante(id, {
      ...Object.fromEntries(Object.entries(campos).map(([k, x]) => [k, x ?? ""])),
      valores,
      orden,
    });
    if (rv.error) return rv;
  }
  for (const v of producto.variantes) {
    if (quedan.has(v.id)) continue;
    const rq = await quitarVariante(v.id);
    if (rq.error) return rq;
  }

  const tener = new Set(f.categoriaIds);
  for (const id of f.categoriaIds) {
    if (producto.categorias.includes(id)) continue;
    const rc = await marcarCategoriaDeProducto(producto.id, id, companyId, true);
    if (rc.error) return rc;
  }
  for (const id of producto.categorias) {
    if (tener.has(id)) continue;
    const rc = await marcarCategoriaDeProducto(producto.id, id, companyId, false);
    if (rc.error) return rc;
  }
  return { error: null };
}

/**
 * A los productos que se acaban de sumar al lanzamiento y ya están en la tienda, la ficha de la tienda.
 * Nunca tira y no frena el alta: si la tienda no se puede leer, quedan con los datos del catálogo y la
 * ficha ofrece "Traer lo de la tienda".
 */
export async function completarConLaTienda(
  lanzamientoId: string,
  companyId: string,
  lanzamientoProductoIds: string[],
): Promise<void> {
  if (lanzamientoProductoIds.length === 0) return;
  try {
    const [{ data: lanz }, { data: filas }] = await Promise.all([
      supabase.from("lanzamientos").select("brand_id, canal, destinos").eq("id", lanzamientoId).maybeSingle(),
      supabase
        .from("lanzamientos_productos")
        .select("id, productos(tn_product_id)")
        .in("id", lanzamientoProductoIds)
        .eq("id_company", companyId),
    ]);
    const destinos = ((lanz as { destinos?: string[] | null; canal?: string } | null)?.destinos ?? [
      (lanz as { canal?: string } | null)?.canal,
    ]) as Array<string | undefined>;
    if (!destinos.includes("tiendanube")) return;
    type Rel = { tn_product_id: number | null } | null;
    const conTienda = ((filas || []) as unknown as Array<{ id: string; productos: Rel | Rel[] }>).flatMap((f) => {
      // La relación llega como objeto o como lista de uno, según cómo la tipe Supabase.
      const rel = Array.isArray(f.productos) ? (f.productos[0] ?? null) : f.productos;
      return rel?.tn_product_id != null ? [{ id: f.id, tn: Number(rel.tn_product_id) }] : [];
    });
    if (conTienda.length === 0) return;
    const brandId = (lanz as { brand_id?: string | null } | null)?.brand_id ?? null;
    const [publicados, arbol] = await Promise.all([
      leerPublicadosEnTn(brandId, conTienda.map((x) => x.tn)),
      fetchCategorias(companyId, brandId),
    ]);
    if (!publicados) return;
    for (const x of conTienda) {
      const p = publicados.get(x.tn);
      // Filas recién creadas: sin variantes ni categorías todavía.
      if (p) await traerLoDeLaTienda({ id: x.id, variantes: [], categorias: [] }, p, arbol, companyId);
    }
  } catch (err) {
    console.error("[traerDeLaTienda] completar con la tienda", err);
  }
}
